import { TradeListLoadingActions, TradeListLoadingStates } from './constants';

export const defaultInitialState = () => ({
  state: TradeListLoadingStates.IDLE,
  metadata: {
    metersCount: 0,
    rulesCount: 0,
    tradesCount: 0,
    error: null,
  },
});

/**
 * Validates and normalizes a count value to ensure it's a non-negative number.
 * @param {*} value - Value to validate
 * @returns {number} Validated count (0 if invalid)
 */
export const validateCount = (value) => (typeof value === 'number' && value >= 0 ? value : 0);

/**
 * Checks if the current state is ERROR, preventing further state transitions.
 * @param {object} state - Current state object
 * @returns {boolean} True if state is ERROR
 */
export const isErrorState = (state) => state?.state === TradeListLoadingStates.ERROR;

/**
 * State machine reducer for the TradeList loading pipeline.
 *
 * This reducer manages the state transitions and context updates for the
 * multi-stage trade data fetching process. It is intentionally side-effect
 * free to simplify testing and reasoning about state changes.
 *
 * State Flow:
 * 1.  IDLE → INITIATE → INITIALISING
 * 2.  INITIALISING → METERS_LOADED(complete: false) → LOADING_METERS
 * 3.  LOADING_METERS → METERS_LOADED(complete: true) → LOADING_RULES
 * 4.  LOADING_RULES → RULES_LOADED(complete: false) → LOADING_RULES
 * 5.  LOADING_RULES → RULES_LOADED(complete: true) → LOADING_TRADES
 * 6.  LOADING_TRADES → TRADES_LOADED(complete: false) → LOADING_TRADES
 * 7.  LOADING_TRADES → TRADES_LOADED(complete: true) → PREPARING_DATA
 * 8.  PREPARING_DATA → DATA_PREPARED → RENDERING_DATA
 * 9.  RENDERING_DATA → DATA_RENDERED → COMPLETE
 * 10. COMPLETE → HIDE_OVERLAY → IDLE
 * 11. Any state → RAISE_ERROR → ERROR (terminal state)
 * 12. Any state → RESET → IDLE
 *
 * Note: Once in ERROR state, only RESET can transition out.
 * @param {object} state - Current state of the machine.
 * @param {object} action - Action to be performed, with associated metadata.
 * @param {string} action.type - Type of action to perform (see TradeListLoadingActions).
 * @param {number} [action.addedMetersCount] - Meter count to add to the internal
 *                                             loaded meters count.
 * @param {number} [action.addedRulesCount] - Rules count to add to the internal
 *                                            loaded rules count.
 * @param {number} [action.addedTradesCount] - Trades count to add to the internal
 *                                             loaded trades count.
 * @param {boolean} [action.complete] - Indicates if the data loaded is the final
 *                                             load for that action.
 * @param {Error} [action.error] - Error for SET_ERROR
 * @returns {object} New state after applying the action
 */
export const loadingStateReducer = (state, action) => {
  switch (action.type) {
    case TradeListLoadingActions.RESET:
      return {
        state: TradeListLoadingStates.IDLE,
        metadata: defaultInitialState().metadata,
      };

    case TradeListLoadingActions.INITIATE:
      // Prevent transition if already in error state.
      if (isErrorState(state)) {
        return state;
      }

      return {
        state: TradeListLoadingStates.INITIALISING,
        metadata: {
          ...state.metadata,
        },
      };

    case TradeListLoadingActions.METERS_LOADED: {
      // Prevent transition if already in error state.
      if (isErrorState(state)) {
        return state;
      }

      const nextState = action.complete
        ? TradeListLoadingStates.LOADING_RULES : TradeListLoadingStates.LOADING_METERS;
      const addedCount = validateCount(action.addedMetersCount);

      return {
        state: nextState,
        metadata: {
          ...state.metadata,
          metersCount: state.metadata.metersCount + addedCount,
        },
      };
    }

    case TradeListLoadingActions.RULES_LOADED: {
      // Prevent transition if already in error state.
      if (isErrorState(state)) {
        return state;
      }

      const nextState = action.complete
        ? TradeListLoadingStates.LOADING_TRADES : TradeListLoadingStates.LOADING_RULES;
      const addedCount = validateCount(action.addedRulesCount);

      return {
        state: nextState,
        metadata: {
          ...state.metadata,
          rulesCount: state.metadata.rulesCount + addedCount,
        },
      };
    }

    case TradeListLoadingActions.TRADES_LOADED: {
      // Prevent transition if already in error state.
      if (isErrorState(state)) {
        return state;
      }

      const nextState = action.complete
        ? TradeListLoadingStates.PREPARING_DATA : TradeListLoadingStates.LOADING_TRADES;
      const addedCount = validateCount(action.addedTradesCount);

      return {
        state: nextState,
        metadata: {
          ...state.metadata,
          tradesCount: state.metadata.tradesCount + addedCount,
        },
      };
    }

    case TradeListLoadingActions.DATA_PREPARED: {
      // Prevent transition if already in error state.
      if (isErrorState(state)) {
        return state;
      }

      return {
        ...state,
        state: TradeListLoadingStates.RENDERING_DATA,
      };
    }

    case TradeListLoadingActions.DATA_RENDERED: {
      // Prevent transition if already in error state.
      if (isErrorState(state)) {
        return state;
      }

      return {
        ...state,
        state: TradeListLoadingStates.COMPLETE,
      };
    }

    case TradeListLoadingActions.HIDE_OVERLAY: {
      // ERROR state should only transition via RESET
      if (isErrorState(state)) {
        return state;
      }

      return {
        state: TradeListLoadingStates.IDLE,
        metadata: defaultInitialState().metadata,
      };
    }

    case TradeListLoadingActions.RAISE_ERROR: {
      return {
        state: TradeListLoadingStates.ERROR,
        metadata: {
          ...state.metadata,
          error: action.error || null,
        },
      };
    }

    default:
      return state;
  }
};
